import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
  IconButton,
  Chip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import api from '../../services/api';
import { AuthContext } from '../../contexts/auth';

const SHIMMER_KEYFRAMES = `
@keyframes admind-shimmer {
  0% { background-position: -200% center; }
  100% { background-position: 200% center; }
}
`;

const LOCALE_MAP = { 0: 'en-GB', 1: 'es-ES', 2: 'pt-BR', 3: 'it-IT' };

function formatDate(isoStr, langCode) {
  if (!isoStr) return '';
  try {
    const locale = LOCALE_MAP[langCode] || 'it-IT';
    return new Date(isoStr).toLocaleDateString(locale, {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  } catch {
    return isoStr;
  }
}

/**
 * forceOpen  – when true the modal opens regardless of modal_dismissed
 * onForceClose – called when the modal closes while in forced-open mode
 */
export default function LegacyMigrationModal({ forceOpen = false, onForceClose, onLegacyStatus }) {
  const { t } = useTranslation();
  const { user } = useContext(AuthContext);
  const [open, setOpen] = useState(false);
  const [info, setInfo] = useState(null);
  const [autoChecked, setAutoChecked] = useState(false);

  const langCode = user?.language ?? 3;

  /* ── Auto-show on first login (modal_dismissed = false) ── */
  useEffect(() => {
    if (!user || autoChecked) return;
    setAutoChecked(true);
    api
      .get('/api/billing/legacy-migration/')
      .then((res) => {
        const data = res.data;
        const isLegacy = !!(data.is_legacy && data.is_active);
        onLegacyStatus?.(isLegacy);
        if (isLegacy) {
          setInfo(data);
          if (!data.modal_dismissed) setOpen(true);
        }
      })
      .catch(() => { onLegacyStatus?.(false); });
  }, [user]);

  /* ── Force-open from sidebar button ── */
  useEffect(() => {
    if (!forceOpen) return;
    if (info) {
      setOpen(true);
    } else if (user) {
      api
        .get('/api/billing/legacy-migration/')
        .then((res) => {
          const data = res.data;
          if (data.is_legacy && data.is_active) {
            setInfo(data);
            setOpen(true);
          } else {
            window.open('https://app.admind.ai', '_blank', 'noopener,noreferrer');
            onForceClose?.();
          }
        })
        .catch(() => {
          window.open('https://app.admind.ai', '_blank', 'noopener,noreferrer');
          onForceClose?.();
        });
    }
  }, [forceOpen]);

  const closeModal = (saveFlag) => {
    if (saveFlag) {
      api.post('/api/billing/legacy-migration/', { action: 'dismiss_modal' }).catch(() => {});
    }
    setOpen(false);
    onForceClose?.();
  };

  const handleDismiss = () => closeModal(true);

  const handleGoOld = () => {
    closeModal(false);
    window.open('https://app.admind.ai', '_blank', 'noopener,noreferrer');
  };

  const expiryFormatted = formatDate(info?.expiry_date_iso, langCode);
  const decommissionFormatted = formatDate(info?.decommission_date_iso, langCode);

  return (
    <>
      <style>{SHIMMER_KEYFRAMES}</style>
      <Dialog
        open={open}
        onClose={handleDismiss}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            background: '#12121e',
            border: '1px solid rgba(112,18,128,0.35)',
            borderRadius: 3,
            color: '#fff',
            overflow: 'hidden',
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            px: 3,
            pt: 3,
            pb: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            position: 'relative',
          }}
        >
          <IconButton
            onClick={handleDismiss}
            size="small"
            sx={{
              position: 'absolute',
              top: 14,
              right: 14,
              color: 'rgba(255,255,255,0.5)',
              '&:hover': { color: '#fff', background: 'rgba(255,255,255,0.08)' },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>

          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: '14px',
              background: 'linear-gradient(135deg, #701280, #C850C0)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 2,
              boxShadow: '0 0 30px rgba(112,18,128,0.4)',
            }}
          >
            <Typography sx={{ fontSize: '1.4rem' }}>✦</Typography>
          </Box>

          <Typography
            variant="h5"
            fontWeight={700}
            textAlign="center"
            sx={{ fontSize: { xs: '1.2rem', sm: '1.45rem' }, lineHeight: 1.3, mb: 0.5 }}
          >
            {t('legacy.title').split('AdMind 2.0')[0]}
            <Box
              component="span"
              sx={{
                background: 'linear-gradient(135deg, #701280 0%, #C850C0 50%, #FF6B35 100%)',
                backgroundSize: '200% auto',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                animation: 'admind-shimmer 4s linear infinite',
              }}
            >
              AdMind 2.0
            </Box>
          </Typography>

          {info && (
            <Chip
              label={info.plan_label}
              size="small"
              sx={{
                mt: 1.5,
                background: 'rgba(112,18,128,0.2)',
                color: '#c97ed9',
                border: '1px solid rgba(112,18,128,0.45)',
                fontWeight: 700,
                fontSize: '0.72rem',
              }}
            />
          )}
        </Box>

        <DialogContent sx={{ px: 3, pt: 1.5, pb: 3 }}>
          <Typography
            variant="body2"
            sx={{ color: 'rgba(255,255,255,0.75)', lineHeight: 1.7, mb: 2, textAlign: 'center' }}
          >
            {t('legacy.body').split('<1>')[0]}
            <Box component="span" sx={{ color: '#c97ed9', fontWeight: 700 }}>
              {info?.plan_label}
            </Box>
            {t('legacy.body').split('</1>')[1]}
          </Typography>

          <Box
            sx={{
              background: 'rgba(255,180,0,0.06)',
              border: '1px solid rgba(255,180,0,0.22)',
              borderRadius: 2.5,
              p: 2,
              mb: 2.5,
            }}
          >
            <Box display="flex" alignItems="center" gap={1} mb={1.2}>
              <WarningAmberIcon sx={{ color: '#f0c040', fontSize: 18 }} />
              <Typography sx={{ color: '#f0c040', fontWeight: 700, fontSize: '0.82rem' }}>
                {t('legacy.warningTitle')}
              </Typography>
            </Box>
            <Box
              component="ul"
              sx={{ m: 0, pl: 2.5, color: 'rgba(255,255,255,0.72)', fontSize: '0.81rem', lineHeight: 2 }}
            >
              <li>
                {t('legacy.item1').split('<1>')[0]}
                <Box component="span" sx={{ color: '#c97ed9', fontWeight: 600 }}>
                  {info?.plan_label}
                </Box>
                {t('legacy.item1').split('</1>')[1]?.split('<2>')[0]}
                <Box component="span" sx={{ fontWeight: 700, color: '#fff' }}>
                  {expiryFormatted}
                </Box>
                {t('legacy.item1').split('</2>')[1]}
              </li>
              <li>
                {t('legacy.item2').split('<1>')[0]}
                <Box component="span" sx={{ fontWeight: 700, color: '#fff' }}>
                  {decommissionFormatted}
                </Box>
                {t('legacy.item2').split('</1>')[1]}
              </li>
              <li>{t('legacy.item3')}</li>
              <li>{t('legacy.item4')}</li>
            </Box>
          </Box>

          <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} gap={1.5}>
            <Button
              variant="outlined"
              startIcon={<OpenInNewIcon />}
              onClick={handleGoOld}
              fullWidth
              sx={{
                borderColor: 'rgba(112,18,128,0.5)',
                color: '#c97ed9',
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.85rem',
                py: 1.1,
                '&:hover': { borderColor: '#701280', background: 'rgba(112,18,128,0.12)' },
              }}
            >
              {t('legacy.goOld')}
            </Button>
            <Button
              variant="contained"
              endIcon={<ArrowForwardIcon />}
              onClick={handleDismiss}
              fullWidth
              sx={{
                background: 'linear-gradient(135deg, #701280 0%, #9b19b0 100%)',
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 700,
                fontSize: '0.85rem',
                py: 1.1,
                boxShadow: '0 4px 20px rgba(112,18,128,0.4)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #5a0f69 0%, #7d158d 100%)',
                  boxShadow: '0 4px 24px rgba(112,18,128,0.55)',
                },
              }}
            >
              {t('legacy.explore')}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
}
